import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Download, Upload, Database, Trash2, RefreshCw, 
  CheckCircle, AlertTriangle, FileArchive, Shield,
  HardDrive, Clock, AlertCircle, Settings, Play, Save
} from 'lucide-react';

export default function BackupTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const fileInputRef = useRef(null);

  // Auto backup config state
  const [autoConfig, setAutoConfig] = useState({ enabled: true, hour: 3, max_backups: 5 });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [triggeringBackup, setTriggeringBackup] = useState(false);

  useEffect(() => {
    fetchBackups();
    fetchSchedulerStatus();
    fetchBackupConfig();
  }, []);

  const fetchBackups = async () => {
    try {
      const res = await api.get('/backup/list');
      setBackups(res.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Erreur lors du chargement des sauvegardes');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const res = await api.get('/backup/scheduler/status');
      setSchedulerStatus(res.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const fetchBackupConfig = async () => {
    try {
      const res = await api.get('/config/singleton/BACKUP_CONFIG');
      if (res.data && res.data.metadata) {
        setAutoConfig({
          enabled: res.data.metadata.enabled !== false,
          hour: parseInt(res.data.metadata.hour) || 3,
          max_backups: Math.min(parseInt(res.data.metadata.max_backups) || 5, 5)
        });
      }
    } catch (error) {
      // Config might not exist yet, use defaults
      console.log('Using default backup config');
    }
  };

  const saveBackupConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/config/singleton/BACKUP_CONFIG', {
        metadata: autoConfig
      });
      toast.success('Configuration sauvegardée');
      fetchSchedulerStatus();
    } catch (error) {
      console.error('Save config error:', error);
      toast.error('Erreur lors de la sauvegarde de la configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const triggerManualBackup = async () => {
    setTriggeringBackup(true);
    try {
      const res = await api.post('/backup/scheduler/trigger');
      if (res.data.success) {
        toast.success(`Sauvegarde créée: ${res.data.backup?.name}`);
        if (res.data.rotation?.deleted > 0) {
          toast.info(`${res.data.rotation.deleted} ancienne(s) sauvegarde(s) supprimée(s)`);
        }
        fetchBackups();
        fetchSchedulerStatus();
      } else {
        toast.error(res.data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Trigger backup error:', error);
      toast.error('Erreur lors du déclenchement de la sauvegarde');
    } finally {
      setTriggeringBackup(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await api.post('/backup/create');
      toast.success(res.data.message || 'Sauvegarde créée avec succès');
      fetchBackups();
    } catch (error) {
      console.error('Create backup error:', error);
      toast.error('Erreur lors de la création de la sauvegarde');
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = (backup) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${backup.download_url}`;
    const token = localStorage.getItem('token');
    
    // Create a temporary link with auth
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = backup.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch(err => {
        console.error('Download error:', err);
        toast.error('Erreur lors du téléchargement');
      });
  };

  const deleteBackup = async (filename) => {
    if (!confirm('Supprimer cette sauvegarde ?')) return;
    
    try {
      await api.delete(`/backup/${filename}`);
      toast.success('Sauvegarde supprimée');
      fetchBackups();
    } catch (error) {
      console.error('Delete backup error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error('Seuls les fichiers ZIP sont acceptés');
      return;
    }
    
    setSelectedFile(file);
    setValidating(true);
    setValidation(null);
    
    try {
      const formData = new FormData();
      formData.append('backup', file);
      
      const res = await api.post('/backup/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setValidation(res.data);
      
      if (res.data.valid) {
        setShowRestoreDialog(true);
      } else {
        toast.error('La sauvegarde est invalide ou corrompue');
      }
    } catch (error) {
      console.error('Validate backup error:', error);
      toast.error('Erreur lors de la validation');
      setSelectedFile(null);
    } finally {
      setValidating(false);
    }
  };

  const executeRestore = async () => {
    if (confirmText !== 'RESTORE') {
      toast.error('Tapez RESTORE pour confirmer');
      return;
    }
    
    if (!selectedFile) {
      toast.error('Aucun fichier sélectionné');
      return;
    }
    
    setRestoring(true);
    
    try {
      const formData = new FormData();
      formData.append('backup', selectedFile);
      formData.append('confirm', 'RESTORE');
      
      const res = await api.post('/backup/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(res.data.message || 'Restauration réussie');
      
      // Show note about server restart
      toast.info('Un rafraîchissement de la page est recommandé', {
        duration: 5000
      });
      
      setShowRestoreDialog(false);
      setSelectedFile(null);
      setValidation(null);
      setConfirmText('');
      fetchBackups();
    } catch (error) {
      console.error('Restore backup error:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la restauration');
    } finally {
      setRestoring(false);
    }
  };

  const cancelRestore = () => {
    setShowRestoreDialog(false);
    setSelectedFile(null);
    setValidation(null);
    setConfirmText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Database className="w-5 h-5" />
            Sauvegarde et restauration
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Créez des sauvegardes complètes du système et restaurez-les si nécessaire
          </p>
        </div>
      </div>

      {/* Automatic Backup Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="w-5 h-5" />
            Sauvegarde automatique
          </CardTitle>
          <CardDescription>
            Configurez une sauvegarde quotidienne automatique avec rotation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status indicator */}
          {schedulerStatus && (
            <div className={`p-3 rounded-lg border flex items-center justify-between ${
              autoConfig.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${autoConfig.enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <span className="text-sm font-medium">
                  {autoConfig.enabled ? 'Scheduler actif' : 'Scheduler désactivé'}
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {schedulerStatus.backup_count} sauvegarde(s) en stock
              </span>
            </div>
          )}

          {/* Configuration form */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Sauvegarde automatique
              </Label>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={autoConfig.enabled}
                  onCheckedChange={(checked) => setAutoConfig(prev => ({ ...prev, enabled: checked }))}
                  data-testid="auto-backup-enabled"
                />
                <span className="text-sm text-muted-foreground">
                  {autoConfig.enabled ? 'Activée' : 'Désactivée'}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Heure d'exécution</Label>
              <Select
                value={autoConfig.hour.toString()}
                onValueChange={(value) => setAutoConfig(prev => ({ ...prev, hour: parseInt(value) }))}
                disabled={!autoConfig.enabled}
              >
                <SelectTrigger data-testid="backup-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, '0')}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Heure locale du serveur</p>
            </div>

            <div className="space-y-2">
              <Label>Conservation max</Label>
              <Select
                value={autoConfig.max_backups.toString()}
                onValueChange={(value) => setAutoConfig(prev => ({ ...prev, max_backups: parseInt(value) }))}
                disabled={!autoConfig.enabled}
              >
                <SelectTrigger data-testid="max-backups-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} sauvegarde{n > 1 ? 's' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Les plus anciennes seront supprimées</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              variant="outline"
              onClick={triggerManualBackup}
              disabled={triggeringBackup}
              data-testid="trigger-auto-backup"
            >
              {triggeringBackup ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Exécution...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Exécuter maintenant
                </>
              )}
            </Button>
            <Button
              onClick={saveBackupConfig}
              disabled={savingConfig}
              data-testid="save-backup-config"
            >
              {savingConfig ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <p className="text-blue-800">
              <strong>Rotation automatique :</strong> Les sauvegardes les plus anciennes seront automatiquement 
              supprimées lorsque le nombre maximum est atteint. Maximum 5 sauvegardes pour éviter de saturer le stockage.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Create Backup Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <HardDrive className="w-5 h-5" />
            Créer une sauvegarde
          </CardTitle>
          <CardDescription>
            La sauvegarde inclut la base de données, les documents uploadés, les photos de passeport et les fiches de police
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button 
              onClick={createBackup} 
              disabled={creating}
              data-testid="create-backup-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Créer une sauvegarde
                </>
              )}
            </Button>
            <div className="text-sm text-muted-foreground">
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Base de données SQLite
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Tous les fichiers uploadés
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Restore Backup Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="w-5 h-5" />
            Restaurer une sauvegarde
          </CardTitle>
          <CardDescription>
            Restaurez le système à partir d'une sauvegarde précédente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                ref={fileInputRef}
                className="hidden"
                id="backup-file-input"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={validating}
                data-testid="select-backup-btn"
              >
                {validating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Validation...
                  </>
                ) : (
                  <>
                    <FileArchive className="w-4 h-4 mr-2" />
                    Sélectionner un fichier
                  </>
                )}
              </Button>
              {selectedFile && !showRestoreDialog && (
                <span className="text-sm text-muted-foreground">
                  {selectedFile.name}
                </span>
              )}
            </div>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Attention</p>
                  <p className="text-sm text-amber-700 mt-1">
                    La restauration remplacera toutes les données actuelles par celles de la sauvegarde.
                    Une sauvegarde de l'état actuel sera automatiquement créée avant la restauration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Backups List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-base">
              <Clock className="w-5 h-5" />
              Sauvegardes existantes
            </div>
            <Button variant="ghost" size="sm" onClick={fetchBackups}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune sauvegarde disponible</p>
              <p className="text-sm">Créez votre première sauvegarde ci-dessus</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div 
                  key={backup.name} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <FileArchive className="w-8 h-8 text-primary" />
                    <div>
                      <p className="font-medium">{backup.name}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{backup.size_mb} MB</span>
                        <span>•</span>
                        <span>
                          {format(new Date(backup.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadBackup(backup)}
                      data-testid={`download-backup-${backup.name}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteBackup(backup.name)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-backup-${backup.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Confirmer la restauration
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de restaurer le système depuis une sauvegarde.
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Validation Summary */}
            {validation && (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm">Fichier sélectionné</p>
                  <p className="text-sm text-muted-foreground">{selectedFile?.name}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg border ${validation.contents.database ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {validation.contents.database ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      Base de données
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${validation.contents.uploads ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {validation.contents.uploads ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      )}
                      Fichiers ({validation.contents.uploads_count})
                    </p>
                  </div>
                </div>
                
                {validation.manifest && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    <p className="font-medium text-blue-800">Informations de sauvegarde</p>
                    <p className="text-blue-700 mt-1">
                      Créée le {format(new Date(validation.manifest.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </p>
                  </div>
                )}
                
                {validation.warnings?.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="font-medium text-amber-800 text-sm">Avertissements</p>
                    <ul className="text-sm text-amber-700 mt-1">
                      {validation.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Confirmation Input */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Tapez <strong className="text-red-600">RESTORE</strong> pour confirmer
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="RESTORE"
                className="font-mono"
                data-testid="restore-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={cancelRestore} disabled={restoring}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={executeRestore}
              disabled={confirmText !== 'RESTORE' || restoring}
              data-testid="confirm-restore-btn"
            >
              {restoring ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Restauration...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Restaurer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
